import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { roleHome } from "../components/ProtectedRoute";
import { getInitialTheme, applyTheme } from "../lib/theme";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { MailCheck, LogOut, RefreshCw } from "lucide-react";

// Verificação por Código — etapa OBRIGATÓRIA antes de "Defina sua nova
// senha" (PrimeiroAcesso.jsx), sempre que must_change_password=true. O
// e-mail é enviado automaticamente ao entrar nesta tela (POST
// /auth/first-access/send-code). O backend é quem garante de verdade que a
// troca de senha não acontece sem essa confirmação (ver change_password) —
// esta tela é só a experiência correta para essa regra.
export default function VerifiqueSeuEmail() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(true);
  const [cooldown, setCooldown] = useState(0);
  const requestedRef = useRef(false);

  useEffect(() => { applyTheme(getInitialTheme()); }, []);

  const requestCode = async () => {
    setErr(""); setSending(true);
    try {
      const { data } = await api.post("/auth/first-access/send-code");
      setMaskedEmail(data.masked_email || "");
      setCooldown(data.resend_cooldown_sec || 60);
    } catch (ex) {
      if (ex?.response?.status === 429) {
        // backend recusou por cooldown — extrai os segundos da própria mensagem amigável, sem inventar contador solto
        const m = /(\d+)\s*segundos/.exec(ex?.response?.data?.detail || "");
        if (m) setCooldown(Number(m[1]));
        setErr(ex.response.data.detail);
      } else if (ex?.response?.status === 401) {
        setErr("Sua sessão expirou. Faça login novamente.");
      } else {
        setErr(formatApiError(ex));
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (user && user.must_change_password && !user.otp_verified && !requestedRef.current) {
      requestedRef.current = true;
      requestCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Carregando...</div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to={roleHome(user.role)} replace />;
  if (user.otp_verified) return <Navigate to="/primeiro-acesso" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Informe os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      await api.post("/auth/first-access/verify-code", { code });
      setUser(u => (u ? { ...u, otp_verified: true } : u));
      toast.success("Código confirmado");
      nav("/primeiro-acesso", { replace: true });
    } catch (ex) {
      if (ex?.response?.status === 429) {
        setErr("Número máximo de tentativas excedido. Solicite um novo código.");
      } else if (ex?.response?.status === 401) {
        setErr("Sua sessão expirou. Faça login novamente.");
      } else {
        setErr(formatApiError(ex));
      }
    } finally {
      setBusy(false);
    }
  };

  const reenviar = async () => {
    if (cooldown > 0 || sending) return;
    setCode("");
    await requestCode();
    if (!err) toast.success("Novo código enviado");
  };

  const sair = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-sm p-6 sm:p-8" data-testid="verifique-email-form">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-primary/10 text-primary">
            <MailCheck size={26} />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground">Verifique seu e-mail</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {maskedEmail
              ? <>Enviamos um código de confirmação para <span className="font-medium text-foreground">{maskedEmail}</span></>
              : "Enviando um código de confirmação para o seu e-mail..."}
          </p>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="va-code">Código de 6 dígitos</Label>
            <Input
              id="va-code" inputMode="numeric" pattern="\d{6}" maxLength={6}
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required autoFocus className="mt-1 text-center text-2xl tracking-[0.5em]"
              placeholder="000000" data-testid="verifique-email-code"
            />
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 dark:bg-red-950 dark:text-red-300 dark:border-red-900" data-testid="verifique-email-error">
              {err}
            </div>
          )}
          <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={busy || sending} data-testid="verifique-email-submit">
            {busy ? "Confirmando..." : "Confirmar código"}
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button
              type="button" onClick={reenviar} disabled={cooldown > 0 || sending}
              className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline flex items-center gap-1"
              data-testid="verifique-email-reenviar"
            >
              <RefreshCw size={13} />
              {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
            </button>
            <button type="button" onClick={sair} className="text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="verifique-email-sair">
              <LogOut size={13} /> Sair
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
